import React, { useState } from 'react';
import AlertBox, { Heading } from 'components/shared/AlertBox';
import { Button, ButtonGroup } from 'ui';
import trackEvent from 'misc/trackEvent';
import Recorder from 'ui/MediaRecorder/Recorder';
import { mediaLocales } from 'components/ui/ZiggeoVideo/ziggeoHelpers';
import MediaPlayer from 'components/ui/MediaPlayer';
import ExtensionMessage from 'components/Practice/shared/ExtensionMessage';
import AutomatedAnalysisAudioMessage from 'components/Practice/shared/AutomatedAnalysisAudioMessage';
import { useFeatureFlags } from 'hooks/index';

const PracticeRecordingQuestion = props => {
  const [isComplete, setIsComplete] = useState(false);
  const [token, setToken] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState({ type: null });
  const [tryAgainModalShown, setTryAgainModalShown] = useState(false);
  const [recordingNotSupported, setRecordingNotSupported] = useState(false);

  const {
    apiKey,
    id,
    resetAnswer,
    setAnswer: setAnswerProp,
    hasZiggeoForAllMedia,
    isPreview,
    hasAutomatedAnalysis,
    isOdp,
  } = props;

  const seismicRecordingEnabled =
    useFeatureFlags('readiness-enable-seismic-recording') && !isOdp;

  const hasNoErrors = !error.type;
  const handleBadUploadType = () => {
    setError({ type: 'upload' });
  };

  const toggleTryAgainModal = () => {
    setTryAgainModalShown(!tryAgainModalShown);
  };

  const tryAgain = () => {
    if (navigator && navigator.onLine) {
      trackEvent('practice/recording_try_again_clicked', {
        content_type: 'on_demand_practice_scenario',
        content_id: id,
      });
      resetAnswer();
      setError({ type: null });
      setIsComplete(false);
      setTryAgainModalShown(false);
    } else {
      setError({ type: 'online' });
    }
  };

  const onRecordingNotSupported = () => {
    if (!recordingNotSupported) {
      setRecordingNotSupported(true);
    }
  };

  const closeErrorModal = () => {
    setError({ type: null });
    tryAgain();
  };

  const setAnswer = data => {
    if (data) {
      const { token: tokenFromData, provider, embedUrl, isSeismic } = data;
      const recordingProvider = isSeismic ? 'seismic' : provider;
      setToken(tokenFromData);
      setUrl(embedUrl);
      if (setAnswerProp) {
        setAnswerProp(tokenFromData, recordingProvider);
      }
    }
    setIsComplete(true);
  };

  const errorHeaderText = () => {
    switch (error.type) {
      case 'offline':
        return t('practice.on_demand_practice.recording.offline_error.title');
      case 'upload':
        return t(
          'practice.on_demand_practice.recording.upload_type_error.title',
        );
      case 'token':
      default:
        return t(
          'practice.on_demand_practice.webcam_recording.token_error.title',
        );
    }
  };

  const errorDescriptionText = () => {
    switch (error.type) {
      case 'offline':
        return t(
          'practice.on_demand_practice.recording.offline_error.description',
        );
      case 'upload':
        return t(
          'practice.on_demand_practice.recording.upload_type_error.description',
        );
      case 'token':
      default:
        return t(
          'practice.on_demand_practice.webcam_recording.token_error.description',
        );
    }
  };

  const recordButtonText = () => {
    const text = seismicRecordingEnabled
      ? t('questions.question.hippo_screen_recording.record')
      : null;
    return text;
  };

  return (
    <>
      <div className="RecordingQuestion VideoEmbed">
        <>
          {hasNoErrors && !isComplete && (
            <>
              {!seismicRecordingEnabled && (
                <ExtensionMessage
                  hasZiggeoForAllMedia={hasZiggeoForAllMedia}
                  recordingNotSupported={recordingNotSupported}
                />
              )}
              <AutomatedAnalysisAudioMessage
                hasAutomatedAnalysis={hasAutomatedAnalysis}
              />
              <Recorder
                allowDragAndDrop={!hasZiggeoForAllMedia}
                apiKey={apiKey}
                webcamPlaybackEnabled
                handleBadUploadType={handleBadUploadType}
                saveMedia={setAnswer}
                mediaLocales={mediaLocales}
                allowUpload
                allowWebcam={false}
                allowScreen
                provider={props.provider}
                onBrowserRecordingNotSupported={onRecordingNotSupported}
                currentUserId={props.currentUserId}
                wide
                trackEvents={props.trackEvents}
                events={props.events}
                progressId={props.progressId}
                questionId={props.questionId}
                disabled={props.disabled || isPreview}
                seismicRecordingEnabled={seismicRecordingEnabled}
                recordButtonText={recordButtonText()}
              />
            </>
          )}
          {recordingNotSupported && !seismicRecordingEnabled && (
            <span className="fs-i">
              {t('lessons.editor.embeds.video.browser_not_supported')}
            </span>
          )}
          {isComplete && (
            <div className="VideoEmbed-player">
              <MediaPlayer
                recording={{
                  externalId: token,
                  provider: props.provider,
                  url,
                }}
                ziggeoApiKey={props.apiKey}
                customClass="Video Video--playable"
                hasZiggeoForAllMedia={hasZiggeoForAllMedia}
              />
            </div>
          )}
        </>

        {isComplete && (
          <ButtonGroup className="mt-16" alignEnd>
            <Button onClick={toggleTryAgainModal}>
              {t('common.try_again')}
            </Button>
          </ButtonGroup>
        )}
      </div>
      {!!error.type && (
        <AlertBox onDismiss={closeErrorModal}>
          <Heading>{errorHeaderText()}</Heading>
          {errorDescriptionText()}
          <ButtonGroup alignEnd className="mt-8">
            <Button customColor onClick={closeErrorModal}>
              {t('common.ok')}
            </Button>
          </ButtonGroup>
        </AlertBox>
      )}
      {tryAgainModalShown && (
        <AlertBox onDismiss={toggleTryAgainModal}>
          <Heading>{t('common.try_again')}</Heading>
          {t('practice.on_demand_practice.webcam_recording.try_again_note')}
          <ButtonGroup alignEnd className="mt-16">
            <Button onClick={toggleTryAgainModal}>{t('common.cancel')}</Button>
            <Button customColor onClick={tryAgain}>
              {t('common.try_again')}
            </Button>
          </ButtonGroup>
        </AlertBox>
      )}
    </>
  );
};

export default PracticeRecordingQuestion;
